import { useLocation } from "react-router";
import { Box } from "@mui/material";
import cx from "classnames";
import { useCurrentUser } from "src/business-hooks/useCurrentUser.tsx";
import { useBuildLink } from "src/hooks/useBuildLink.ts";
import { useGlobalLocalContext } from "src/storage/localStorage.ts";
import { appRoutes } from "src/utils/appRoutes/appRoutes";
import SidebarExpandCollapseButton from "./components/SidebarExpandCollapseButton/SidebarExpandCollapseButton.tsx";
import SidebarNavigationItem from "./components/SidebarNavigationItem/SidebarNavigationItem.tsx";
import SidebarSectionTitle from "./components/SidebarSectionTitle/SidebarSectionTitle.tsx";
import {
  SIDEBAR_ADMIN_ENTRIES,
  SIDEBAR_MAIN_ENTRIES,
  type SidebarEntry,
  type SidebarVisibilityContext,
} from "./Sidebar.constants.ts";
import { SIDEBAR_ENTRY_ICONS } from "./Sidebar.icons.tsx";
import "./Sidebar.css";

export const Sidebar = () => {
  const buildLink = useBuildLink();
  const isCollapsed = useGlobalLocalContext((s) => s.sideBarCollapsed);
  const isDebugMode = useGlobalLocalContext((state) => state.isDebugMode);
  const { pathname } = useLocation();

  const loggedInUser = useCurrentUser();
  const visibility: SidebarVisibilityContext = {
    isAdmin: !!loggedInUser?.is_tenant_admin,
    isSuperAdmin: !!loggedInUser?.is_super_admin,
    isDebugMode,
  };

  const visibleAdminEntries = SIDEBAR_ADMIN_ENTRIES.filter((entry) =>
    entry.isVisible(visibility)
  );

  const renderEntry = (entry: SidebarEntry) => {
    const Icon = SIDEBAR_ENTRY_ICONS[entry.routeKey];
    if (!Icon) return null;
    return (
      <SidebarNavigationItem
        key={entry.routeKey}
        title={entry.label}
        to={entry.getTo(buildLink)}
        Icon={Icon}
        isActive={appRoutes[entry.routeKey].matcher(pathname)}
        isCollapsed={isCollapsed}
      />
    );
  };

  return (
    <Box
      data-test-id="side-bar"
      className={cx(
        "sidebar flex flex-col z-20 border-r default-border-color sticky overflow-hidden top-(--top-bar-height)"
      )}
      sx={{
        height: "calc(100vh - var(--top-bar-height))",
      }}
      {...(isCollapsed ? { "data-collapsed": true } : {})}
    >
      <section
        className={cx("flex-1 flex flex-col gap-y-[2vh] py-7", {
          "px-4": !isCollapsed,
          "px-2": isCollapsed,
        })}
      >
        <SidebarSectionTitle>MAIN</SidebarSectionTitle>
        {SIDEBAR_MAIN_ENTRIES.filter((entry) => entry.isVisible(visibility)).map(
          renderEntry
        )}

        {visibleAdminEntries.length > 0 && (
          <SidebarSectionTitle>ADMIN</SidebarSectionTitle>
        )}
        {visibleAdminEntries.map(renderEntry)}
      </section>

      <SidebarExpandCollapseButton className="absolute left-5 bottom-3 z-1 text-grey-400" />
    </Box>
  );
};
